import React, { useState, useMemo } from 'react';
import { Typography, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './PnLCalendar.css';

const PnLCalendar = ({ entries = [], onDayClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Group entries by date
  const entriesByDate = useMemo(() => {
    const grouped = {};
    entries.forEach(entry => {
      if (!entry.trade_date) return;
      const date = new Date(entry.trade_date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      if (!grouped[key]) {
        grouped[key] = { trades: [], totalPL: 0 };
      }
      grouped[key].trades.push(entry);
      grouped[key].totalPL += entry.profit_loss || 0;
    });
    return grouped;
  }, [entries]);

  // Monthly stats
  const monthlyStats = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    let totalPL = 0;
    let trades = 0;
    let winDays = 0;

    Object.entries(entriesByDate).forEach(([dateKey, data]) => {
      const [y, m] = dateKey.split('-').map(Number);
      if (y === year && m === month + 1) {
        totalPL += data.totalPL;
        trades += data.trades.length;
        if (data.totalPL > 0) winDays++;
      }
    });

    return { totalPL, trades, winDays };
  }, [entriesByDate, currentDate]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const formatPL = (value) => {
    const abs = Math.abs(value);
    const sign = value >= 0 ? '+' : '-';
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="cal-day empty" />);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = entriesByDate[dateKey];
      const isToday = today.getDate() === day &&
                      today.getMonth() === currentDate.getMonth() &&
                      today.getFullYear() === currentDate.getFullYear();

      let className = 'cal-day';
      if (isToday) className += ' today';
      if (dayData) {
        className += dayData.totalPL > 0 ? ' profit' : dayData.totalPL < 0 ? ' loss' : '';
      }

      days.push(
        <Tooltip
          key={day}
          title={dayData ? `${dayData.trades.length} trade${dayData.trades.length > 1 ? 's' : ''}` : ''}
          arrow
          placement="top"
        >
          <div
            className={className}
            onClick={() => dayData && onDayClick?.(dateKey, dayData)}
          >
            <span className="day-num">{day}</span>
            {dayData && (
              <span className={`day-pl ${dayData.totalPL >= 0 ? 'positive' : 'negative'}`}>
                {formatPL(dayData.totalPL)}
              </span>
            )}
          </div>
        </Tooltip>
      );
    }

    return days;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="pnl-calendar">
      {/* Header */}
      <div className="cal-header">
        <div className="cal-nav">
          <IconButton onClick={() => navigateMonth(-1)} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography className="cal-title">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
          <IconButton onClick={() => navigateMonth(1)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </div>

        <div className="cal-stats">
          <span className={`stat-pl ${monthlyStats.totalPL >= 0 ? 'positive' : 'negative'}`}>
            {formatPL(monthlyStats.totalPL)}
          </span>
          <span className="stat-divider">•</span>
          <span className="stat-trades">{monthlyStats.trades} trades</span>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="cal-weekdays">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="weekday">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="cal-grid">
        {renderCalendar()}
      </div>
    </div>
  );
};

export default PnLCalendar;
